import { AuthService, Season } from '../auth/auth.service';
import { EgpSeason, EgpService } from '../egp/egp.service';
import { LanguageService } from '../shared/services/language.service';
import {
  WarningService,
  WarnRegion,
  WarnSeason,
} from '../warning/warning.service';
import { ArchiveService } from './archive.service';

import { GetLocaleFromLangPipe } from '../pipes/get-locale-from-lang.pipe';
import { DatexPipe } from '../shared/datex.pipe';
import { KeyPipe } from '../shared/key.pipe';
import { SplitStringPipe } from '../shared/split-string.pipe';

import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  inject,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatListModule } from '@angular/material/list';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { FilteredEppProduct } from './archive.types';
import { CsvModule } from '@ctrl/ngx-csv';
import * as FileSaver from 'file-saver';
import JSZip from 'jszip';
import * as JSZipUtils from 'jszip-utils';
import moment from 'moment';
import { Moment } from 'moment';
import { startWith } from 'rxjs';
export interface DateArray {
  year: number;
  month: number;
  day: number;
}

export interface Customer {
  customerId: number;
  customerName: string;
}

export const MODULES = [
  CommonModule,
  MatFormFieldModule,
  MatDatepickerModule,
  ReactiveFormsModule,
  MatButtonModule,
  MatCardModule,
  CsvModule,
  MatTooltipModule,
  MatSelectModule,
  MatListModule,
  MatDividerModule,
  MatPaginatorModule,
  RouterModule,
  MatTabsModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [
  DatexPipe,
  KeyPipe,
  SplitStringPipe,
  GetLocaleFromLangPipe,
];

@Component({
  selector: 'app-archive',
  templateUrl: './archive.component.html',
  styleUrls: ['./archive.component.scss'],
  imports: [...MODULES, ...COMPONENTS, ...PIPES],
})
export class ArchiveComponent implements OnInit, AfterViewInit {
  public auth = inject(AuthService);
  private translate = inject(TranslateService);
  private archiveService = inject(ArchiveService);
  private warningService = inject(WarningService);
  private egpService = inject(EgpService);
  public languageService = inject(LanguageService);

  customerDocument: any;

  // PRODUCT TYPES 8 to translate and re-translate)
  eopTypes = {
    TWO_DAY: ['eop2'],
    FOUR_DAY: ['eop4'],
    SIX_DAY: ['eop6'],
    EIGHT_DAY: ['eop8'],
    TWO_DAY_SUMMER: ['eops2'],
    FOUR_DAY_SUMMER: ['eops4'],
    SIX_DAY_SUMMER: ['eops6'],
    EIGHT_DAY_SUMMER: ['eops8'],
    TWO_DAY_ALL: ['eopa2'],
    FOUR_DAY_ALL: ['eopa4'],
    SIX_DAY_ALL: ['eopa6'],
    EIGHT_DAY_ALL: ['eopa8'],
  };
  egpTypes = {
    ZERO_DAY: ['egp0'],
    TWO_DAY: ['egp2'],
    FOUR_DAY: ['egp4'],
    TWO_DAY_ALL: ['egpa2'],
    FOUR_DAY_ALL: ['egpa4'],
  };
  egpTypesSummer = {
    ZERO_DAY: ['egps0'],
    TWO_DAY: ['egps2'],
    FOUR_DAY: ['egps4'],
    TWO_DAY_ALL: ['egpas2'],
    FOUR_DAY_ALL: ['egpas4'],
  };
  eopTypesTranslate = {
    eop2: 'TWO_DAY',
    eop4: 'FOUR_DAY',
    eop6: 'SIX_DAY',
    eop8: 'EIGHT_DAY',
    eops2: 'TWO_DAY_SUMMER',
    eops4: 'FOUR_DAY_SUMMER',
    eops6: 'SIX_DAY_SUMMER',
    eops8: 'EIGHT_DAY_SUMMER',
    eopa2: 'TWO_DAY_ALL',
    eopa4: 'FOUR_DAY_ALL',
    eopa6: 'SIX_DAY_ALL',
    eopa8: 'EIGHT_DAY_ALL',
  };

  // Calendar-Properties
  dateRange = new FormGroup({
    start: new FormControl(null, Validators.required),
    end: new FormControl(null, Validators.required),
  });

  private formStatus = toSignal(this.dateRange.statusChanges, {
    initialValue: this.dateRange.status,
  });

  dateRangeInvalid = computed(() => this.formStatus() === 'INVALID');

  private startStatusSig = toSignal(
    this.dateRange.controls.start.statusChanges.pipe(
      startWith(this.dateRange.controls.start.status)
    )
  );

  private endStatusSig = toSignal(
    this.dateRange.controls.end.statusChanges.pipe(
      startWith(this.dateRange.controls.end.status)
    )
  );

  startHasMatError = computed(() => {
    this.startStatusSig();
    return this.dateRange.controls.start.hasError('matStartDateInvalid');
  });

  endHasMatError = computed(() => {
    this.endStatusSig();
    return this.dateRange.controls.end.hasError('matEndDateInvalid');
  });

  startHasMatRequiredError = computed(() => {
    this.startStatusSig();
    return this.dateRange.controls.start.hasError('required');
  });

  endHasMatRequiredError = computed(() => {
    this.endStatusSig();
    return this.dateRange.controls.end.hasError('required');
  });

  allowedDates = [];
  today = moment(moment().format('YYYY-MM-DD 00:00:00'));
  /*localTranslation = CalenderTranslation;*/

  // CSV PROPERTIES
  //
  // Bug (gemeldet): Download erzeugte eine leere CSV-Datei. Ursache: csvLink
  // (@ctrl/ngx-csv) baut den eigentlichen Download-Link (href-Attribut,
  // data:-URI) NICHT bei Klick live neu, sondern reagiert per ngOnChanges()
  // auf @Input()-Änderungen von data/headers/filename – das passiert erst
  // in der NÄCHSTEN Change-Detection-Runde. War onCreateCSV() (wie vorher)
  // an denselben Klick gebunden, der auch den Download auslöst, folgte der
  // Browser dem <a href="...">-Attribut, das zu diesem Zeitpunkt noch den
  // ALTEN (beim allerersten Klick: leeren) Stand hatte – die frisch von
  // onCreateCSV() gesetzten Werte kamen immer erst einen Klick zu spät an.
  // Fix: csvHeader/csvData/csvFileName sind jetzt computed() – sie sind
  // dadurch schon VOR jedem Klick aktuell, sobald sich Auswahl/Zeitraum/
  // Daten ändern, nicht erst dadurch.
  csvHeader = computed(() => [
    { label: 'Datum/Zeit', key: 'dtg' },
    { label: 'Typ', key: 'type' },
    { label: 'Inhalt', key: 'message' },
  ]);

  private dateRangeValue = toSignal(this.dateRange.valueChanges, {
    initialValue: this.dateRange.value,
  });

  csvFileName = computed(() => {
    const areaId = this.selectedWarningAreaId();
    const areaName = this.warningStationNames()[areaId] ?? '';
    const range = this.dateRangeValue();
    if (!range?.start || !range?.end) {
      return areaName;
    }
    const timeRange =
      '(' +
      range.start.format('DD/MM/YYYY') +
      ' – ' +
      range.end.format('DD/MM/YYYY') +
      ')';
    return areaName + ' ' + timeRange;
  });

  csvData = computed(() =>
    this.createCsvDataFromFilteredWarnings(this.selectedWarningAreaId())
  );

  // EOP Properties
  eopProducts; // holds all eopProducts filtered out of customerDocument
  eopDataGroupedByLocationId = signal<any>({});
  selectedEopStationId = model<any>(undefined);
  selectedEopType = model<any>(undefined);
  eopCardHasContent = signal(false);

  // EGP PROPERTIES
  egpProducts; // holds all eopProducts filtered out of customerDocument
  egpDataGroupedByLocationId = signal<any>({});
  selectedEgpStationId = model<any>(undefined);
  selectedEgpType = signal<any>(undefined);
  egpCardHasContent = signal(false);

  // EGP SUMMER PROPERTIES
  egpSummerProducts; // holds all eopProducts filtered out of customerDocument
  egpSummerApiData;
  egpSummerDataGroupedByLocationId = signal<any>({});
  selectedEgpSummerStationId = model<any>(undefined);
  selectedEgpSummerType = signal<any>(undefined);
  egpSummerCardHasContent = signal(false);

  // EPP PROPERTIES
  eppProducts: FilteredEppProduct | null;
  eppDataGroupedByLocationId = signal<any>({});
  selectedEppStationId = model<any>(undefined);
  selectedEppType = model<any>(undefined);
  eppCardHasContent = signal(false);

  // EGW PROPERTIES
  egwProducts; // Array of all areaId's for which customer receives warnings

  // EGW SUMMER PROPERTIES
  egwSummerProducts; // Array of all id's

  // TRANSLATION FILES (storing id: name of stations/areas)
  egpAreaNames = signal<Record<string, string>>(undefined); // needed to translate egp(winter) areaId to name
  egpSummerAreaNames = signal<Record<string, string>>(undefined);
  eopStationNames = signal<Record<string, string>>(undefined);
  eppStationNames = signal<Record<string, string>>(undefined);
  warningStationNames = signal({}); // holds for all stationId and corresponding names for warnings

  // WARNINGS
  warningsForAllEgwAndEgwSummerProducts = {}; // holds all warningsForAllEgwAndEgwSummerProducts
  filteredWarnings = signal({});
  warningCardHasContent = signal(false);
  csvWarnings = {};
  selectedWarningAreaId = model<string>(undefined);
  warningAreasAll = [];
  warnSeason: boolean;
  paginatorDataWarnings = signal<any[]>([]); // paginator for warnings (sliced data to show)

  // PANEL WHEN NO DATA HAS BEEN REQUESTED
  noDataHasBeenRequested = signal(true);

  hasUserNLContent = computed(() => {
    const customerDocument = this.auth.getCustomerDocument();
    return customerDocument?.customerId === 15001;
  });

  dateFilter = (d: Moment | null): boolean => {
    return this.allowedDates.indexOf(d.unix()) >= 0;
  }; // for calendar --> only shows dates in calendar that are in allowedDates

  ngOnInit(): void {
    /*TODO: Übersetzung für Kalender einbauen*/
    /*this.calenderLanguage = this.localTranslation[localStorage.getItem('language')];
    moment.locale(localStorage.getItem('language'));*/
    this.customerDocument = this.auth.getCustomerDocument();
    this.getDatesForCalendar();

    this.getArchiveProducts();
  }

  async ngAfterViewInit(): Promise<void> {
    // GET TRANSLATIONS FOR PRODUCTS ( stationId's and corresponding names)
    // EOP
    // get eopStationIds & corresponding names, needed to translate id --> stationNames
    this.eopStationNames.set({
      ...this.getEopStationsNames(this.customerDocument),
    });
    // EPP
    this.eppStationNames.set({
      ...this.getEppStationsNames(this.customerDocument),
    });

    // EGP
    // returns object with egpArea data for winter-areas (egpAreaData) & summer-areas(egpSummerAreaData)
    // out of which the stationIds + corresponding names will be acquired
    const egpAreaData = await this.getEgpAreasNames();
    // sorts 'id: names' from egp & egpSummer products into objects, they are later used for translating id to real station names
    // Translationfiles: egp(winter) --> egpAreaNames ; egpSummer --> egpSummerAreaNames
    this.sortOutEgpAreaNames(egpAreaData);

    // GET WARNINGS
    // get warningData in advance, when data gets queried (with startDate & endDate) in onQueryData warning get filtered
    this.getWarningsFromApi();
    this.getWarningsSummerFromApi();
  }

  /*=================== BASIC FUNCTIONS ========================*/

  getDatesForCalendar(): void {
    const dates = [];
    const customerDocument = this.auth.getCustomerDocument();
    // for every product
    for (const product of [
      'eppProducts',
      'eopProducts',
      'egpProducts',
      'egpSummerProducts',
      'egwProducts',
      'egwSummerProducts',
    ]) {
      if (customerDocument.hasOwnProperty(product)) {
        const singleMainProduct = customerDocument[product]; // e.g. eopProducts
        // was it ever active?
        // TODO: eventuell product.id in this.auth.isProductActive wieder reinnehmen?
        if (this.auth.isProductActive(product, undefined, true)) {
          // for every SubProduct within the MainProduct-Object
          for (const singleProductType of singleMainProduct) {
            const startDate = moment(
              moment(singleProductType['contractStartDtg']).format(
                'YYYY-MM-DD 00:00:00'
              )
            );
            let endDate = this.today;

            // when product has contractExpiryDtg
            if (singleProductType['contractExpiryDtg']) {
              endDate = moment(
                moment(singleProductType['contractExpiryDtg']).format(
                  'YYYY-MM-DD 00:00:00'
                )
              );
              if (endDate.isAfter(this.today)) {
                endDate = this.today;
              }
            }

            // check each day between start and expiry date
            for (
              const dtg = startDate;
              dtg.isBefore(endDate) || dtg.isSame(endDate);
              dtg.add(1, 'day')
            ) {
              const timeStamp = dtg.unix();
              if (dates.indexOf(timeStamp) < 0) {
                // check season
                if (this.isDateInSeason(dtg, singleProductType['saison'])) {
                  dates.push(timeStamp);
                }
              }
            }
          }
        }
      }
      this.allowedDates = dates;
    }
  }

  isDateInSeason(day: Moment, season: Season): boolean {
    if (!season) {
      return true;
    }
    // convert to moment
    const start: Moment = moment({
      year: day.year(),
      month: Number(season.start.month) - 1,
      day: Number(season.start.day),
    });
    const end: Moment = moment({
      year: day.year(),
      month: Number(season.end.month) - 1,
      day: Number(season.end.day),
    });

    if (start.isSameOrBefore(end)) {
      // start <= end, z.B. 01.04.-30.10.
      return !(day.isBefore(start) || day.isAfter(end));
    }
    // start > end, z.B. 01.11.-30.04.
    if (day.isSameOrAfter(start) || day.isSameOrBefore(end)) {
      // z.B. today = 05.12. || today = 24.03
      return true;
    }
    return false;
  }

  onQueryData(
    startDate = this.dateRange.value.start,
    endDate = this.dateRange.value.end
  ): void {
    // NOTE: one day added to dtgEnd to really get pdf till the date picked in calendar,
    //       e.g endDate picked in calendar is 24.01, without adding one day API would only deliver PDF's till 23.01
    // ----------------------------------------------------------------------
    const dtgStart = startDate.format('YYYY-MM-DDT00:00:00');
    // NOTE: 1 days added to really get the pdfs of the chosen day in calendar
    const dtgEnd = endDate.add(1, 'days').format('YYYY-MM-DDT00:00:00');

    // RESET CARDS
    this.noDataHasBeenRequested.set(false);
    this.warningCardHasContent.set(false);
    this.eopCardHasContent.set(false);
    this.eppCardHasContent.set(false);
    this.egpSummerCardHasContent.set(false);
    this.egpCardHasContent.set(false);

    // EOP
    if (this.eopProducts !== null) {
      this.requestDataForProducts(dtgStart, dtgEnd, this.eopProducts).then(
        eopApiData => {
          // sort apiData for stationId: {productType:[{pdf}, {pdf}, ...]}
          this.eopDataGroupedByLocationId.set({
            ...this.sortApiDataByLocation(eopApiData),
          });
          const eopDataGroupedByLocationId = this.eopDataGroupedByLocationId();
          // set initial selected values for eop
          this.setInitialSelectedStationAndType(
            'eop',
            eopDataGroupedByLocationId
          );
          // check if eop card should be shown or not
          this.eopCardHasContent.set(
            this.checkForContent(eopDataGroupedByLocationId)
          );
        }
      );
    }

    // EGP
    if (this.egpProducts !== null) {
      this.requestDataForProducts(dtgStart, dtgEnd, this.egpProducts).then(
        egpApiData => {
          this.egpDataGroupedByLocationId.set({
            ...this.sortApiDataByLocation(egpApiData),
          });

          this.setInitialSelectedStationAndType(
            'egp',
            this.egpDataGroupedByLocationId()
          );
          this.egpCardHasContent.set(
            this.checkForContent(this.egpDataGroupedByLocationId())
          );
        }
      );
    }

    // EGP SUMMER
    if (this.egpSummerProducts !== null) {
      this.requestDataForProducts(
        dtgStart,
        dtgEnd,
        this.egpSummerProducts
      ).then(egpSummerApiData => {
        this.egpSummerDataGroupedByLocationId.set({
          ...this.sortApiDataByLocation(egpSummerApiData),
        });
        const egpSummerDataGroupedByLocationId =
          this.egpSummerDataGroupedByLocationId();
        this.setInitialSelectedStationAndType(
          'egpSummer',
          egpSummerDataGroupedByLocationId
        );
        this.egpSummerCardHasContent.set(
          this.checkForContent(egpSummerDataGroupedByLocationId)
        );
      });
    }

    // EPP
    if (this.eppProducts !== null) {
      this.requestDataForEpp(dtgStart, dtgEnd, this.eppProducts).then(
        eppApiData => {
          this.eppDataGroupedByLocationId.set({
            ...this.sortApiDataByLocation(eppApiData),
          });
          const eppDataGroupedByLocationId = this.eppDataGroupedByLocationId();
          this.deleteEppStationsThatAreNotInCustomerDocument(
            this.customerDocument,
            eppDataGroupedByLocationId
          );

          this.setInitialSelectedStationAndType(
            'epp',
            eppDataGroupedByLocationId
          );

          this.eppCardHasContent.set(
            this.checkForContent(eppDataGroupedByLocationId)
          );
        }
      );
    }

    // WARNING
    if (Object.keys(this.warningsForAllEgwAndEgwSummerProducts).length) {
      this.filterWarnings(
        this.warningsForAllEgwAndEgwSummerProducts,
        dtgStart,
        dtgEnd
      );
      const filteredWarnings = this.filteredWarnings();
      this.warningCardHasContent.set(
        this.checkFilteredWarningsForData(filteredWarnings)
      );
      if (this.warningCardHasContent()) {
        this.setInitialSelectedStationAndType('egw', filteredWarnings);
        this.initTableData();
      }
    }

    // reset date subtract the 1 day added at start of this function
    endDate.subtract(1, 'days');
  }

  checkFilteredWarningsForData(
    filteredWarnings: object = this.filteredWarnings()
  ): boolean {
    let filteredWarningsHoldsData = false;
    const allAreaIds = Object.keys(filteredWarnings);

    for (const singleAreaId of allAreaIds) {
      if (filteredWarnings[singleAreaId].length) {
        filteredWarningsHoldsData = true;
      }
    }
    return filteredWarningsHoldsData;
  }

  getArchiveProducts(): void {
    // EXPLANATION: filters all the products defined in productsToGet out of the customerDocument and puts them in their
    // global property (e.g. egpProducts)
    // transformProductTypes --> translates ProductTypes from e.g. TWO_DAYS to eop2
    const productsToGet = [
      'eopProducts',
      'egpProducts',
      'egpSummerProducts',
      'egwProducts',
      'egwSummerProducts',
      'eppProducts',
    ];

    for (const productGroupName of productsToGet) {
      if (this.customerDocument[productGroupName].length <= 0) {
        this[productGroupName] = null;
        continue;
      } // skip loop if product in customerDocument is empty

      switch (productGroupName) {
        case 'eopProducts':
          // get all eopProducts from customerDocument
          let eopProductsInCustomerDocument =
            this.filterOutProductsFromCustomerDocument(
              this.customerDocument,
              productGroupName
            );
          // add eop stations to productId's
          eopProductsInCustomerDocument = this.addEopStationsToProductId(
            this.customerDocument,
            eopProductsInCustomerDocument
          );
          // translate eopTypes from e.g. EIGHT_DAY --> eop8; REASON: API needs productType like this (eop8, eop2, ...)
          this.eopProducts = this.transformProductTypes(
            'eopProducts',
            eopProductsInCustomerDocument
          );
          break;

        case 'egpProducts':
          const egpProductsInCustomerDocument =
            this.filterOutProductsFromCustomerDocument(
              this.customerDocument,
              productGroupName
            );
          this.egpProducts = this.transformProductTypes(
            'egpProducts',
            egpProductsInCustomerDocument
          );
          break;

        case 'egpSummerProducts':
          const egpSummerProductsInCustomerDocument =
            this.filterOutProductsFromCustomerDocument(
              this.customerDocument,
              productGroupName
            );
          this.egpSummerProducts = this.transformProductTypes(
            'egpSummerProducts',
            egpSummerProductsInCustomerDocument
          );
          break;

        case 'egwProducts':
          this.egwProducts = this.filterOutProductsFromCustomerDocument(
            this.customerDocument,
            productGroupName
          );
          break;

        case 'egwSummerProducts':
          this.egwSummerProducts = this.filterOutProductsFromCustomerDocument(
            this.customerDocument,
            productGroupName
          );
          break;

        case 'eppProducts':
          this.eppProducts = this.filterOutEppProductFromCustomerDocument(
            this.customerDocument,
            productGroupName
          );
          break;

        default:
          console.log(
            'Error in product getArchiveProducts(), product not defined'
          );
      }
    }
  }

  requestDataForProducts(
    dtgStart,
    dtgEnd,
    productToBeRequestedFromApi: object
  ): Promise<object[]> {
    // sends request for every product in productGroup
    const customerId = parseInt(this.customerDocument['customerId'], 10);

    const dataOfRequestedProducts = [];
    for (const [key, value] of Object.entries(productToBeRequestedFromApi)) {
      const productId = parseInt(key, 10);

      // NOTE: eopData has different structure, in case of eopData 'value' is an object like this --> {productType: [all stations]}
      //       that's why differentiation on how to get the productType from productToBeRequestedFromApi is needed;
      //       Object.keys(value)[0] --> eopData ([0] because Object.keys(value) returns an array with all keys of value &
      //                                 productId only has one productType);
      //       value.toString()      --> egpData & egpSummerData
      const productType =
        typeof value === 'object' && value !== null
          ? Object.keys(value)[0]
          : value?.toString();

      dataOfRequestedProducts.push(
        new Promise((resolve, reject) => {
          this.archiveService
            .getProductData(
              productType,
              customerId,
              productId,
              dtgStart,
              dtgEnd
            )
            .subscribe(
              data => {
                resolve(data);
              },
              error =>
                console.log(
                  'Error Archive Component requestDataForProducts',
                  error
                )
            );
        })
      );
    }
    return Promise.all(dataOfRequestedProducts);
  }

  filterOutProductsFromCustomerDocument(
    customerDocument: object,
    productGroupToFilterOut: string
  ): object | Array<number> {
    /* EXPLANATION: returns allProductsOfProductGroup, which contains all id's (productId's) and their corresponding  productType
                    allProductsOfProductGroup should look like this --> allProductsOfProductGroup = { id: eopType, id: eopType, ... }
                    keyForProductType --> needed to access productType (e.g. eopType)
        NOTE: productGroup has to be the name which is given in customerDocument --> e.g. eopProducts */

    // keyForProductType --> e.g. eopType or egpType ...
    // for products with no productType returns 'ProductWithNoProductType' (e.g. egw & egwSummer)
    const keyForProductType = this.getProductTypeKey(productGroupToFilterOut);
    const productGroup = customerDocument[productGroupToFilterOut];

    /*const allProductsOfProductGroup = {}; // structure should look like this
    e.g. for eopProducts --> allProductsOfProductGroup = { id: eopType, id: eopType,.. }*/
    let allProductsOfProductGroup;

    switch (productGroupToFilterOut) {
      case 'eopProducts':
      case 'egpProducts':
      case 'egpSummerProducts':
        allProductsOfProductGroup = {};
        for (const singleProduct of productGroup) {
          if (
            !singleProduct.active ||
            this.checkForUnwantedProductTypes(singleProduct, keyForProductType)
          ) {
            continue;
          }
          // adds 'id : productType' to allProductsOfProductGroup
          if (!allProductsOfProductGroup.hasOwnProperty(singleProduct.id)) {
            const productId = singleProduct.id;
            const productType = singleProduct[keyForProductType];
            allProductsOfProductGroup[productId] = productType;
          }
        }
        break;

      case 'egwProducts':
        allProductsOfProductGroup = [];
        for (const singleProduct of productGroup) {
          const areaId = singleProduct.areaId;
          allProductsOfProductGroup.push(areaId);
        }
        break;

      case 'egwSummerProducts':
        allProductsOfProductGroup = [];
        for (const singleProduct of productGroup) {
          const id = singleProduct.id;
          allProductsOfProductGroup.push(id);
        }
        break;

      default:
        console.log(
          'Error: productGroupToFilterOut not defined in filterOutProductsFromCustomerDocument()'
        );
    }
    return allProductsOfProductGroup;
  }

  /* EPP */
  filterOutEppProductFromCustomerDocument(
    customerDocument: object,
    productGroupToFilterOut: string
  ): FilteredEppProduct {
    // no eppProducts --> then eppProducts should be set to NULL from backend
    if (customerDocument[productGroupToFilterOut] === null) {
      return null;
    }
    // [0] because eppProducts Array should only ever contain one object with alle the epp definitions
    const eppProduct = customerDocument[productGroupToFilterOut][0];

    // get timeResolutions(days) of epp (needed to build string for API request e.g. epp4 or epp8)
    const eppResolutionsToAskFor = eppProduct['resolutions'].map(
      singleResolution => singleResolution.days
    );

    const timeResolutionsCombineWithParameterSetNames = [];

    // combine timeResolutions with parameter_set names
    for (const singleParameterSetName of eppProduct['parameter_sets']) {
      for (const singleResolution of eppResolutionsToAskFor) {
        const completeEppNameForApiRequest =
          'epp' + singleResolution + singleParameterSetName;
        timeResolutionsCombineWithParameterSetNames.push(
          completeEppNameForApiRequest
        );
      }
    }

    // add productId and corresponding timeResolutions
    const filteredEppProduct = {};
    filteredEppProduct[eppProduct.id] =
      timeResolutionsCombineWithParameterSetNames;
    return filteredEppProduct;
  }

  requestDataForEpp(
    dtgStart,
    dtgEnd,
    eppProductWithNeededTimeResolutions: FilteredEppProduct | null
  ) {
    if (eppProductWithNeededTimeResolutions === null) {
      return;
    }

    const customerId = parseInt(this.customerDocument['customerId'], 10);

    const dataOfRequestedProducts = [];
    // pro eppProduct
    const productIdAsString = Object.keys(
      eppProductWithNeededTimeResolutions
    )[0];

    const eppsToRequest =
      eppProductWithNeededTimeResolutions[productIdAsString];
    // cast productId into number (needed as number for API request)
    const productId = parseInt(productIdAsString, 10);

    const promisesForEachTimeResolutionOfSingleEppProduct = [];
    for (const productType of eppsToRequest) {
      promisesForEachTimeResolutionOfSingleEppProduct.push(
        new Promise((resolve, reject) => {
          this.archiveService
            .getProductData(
              productType,
              customerId,
              productId,
              dtgStart,
              dtgEnd
            )
            .subscribe(
              data => {
                resolve(data);
              },
              error =>
                console.log('Error Archive Component requestDataForEpp', error)
            );
        })
      );
    }
    return Promise.all(promisesForEachTimeResolutionOfSingleEppProduct);
  }
  // work around
  deleteEppStationsThatAreNotInCustomerDocument(
    customerDocument,
    eppDataGroupedByLocationId
  ) {
    const eppStationsInCustomerDocument = customerDocument['eppProducts'][0][
      'stations'
    ].map(singleStation => singleStation['stationId']);
    const allStationsFromApi = Object.keys(eppDataGroupedByLocationId);

    for (const singleEppStationFromApi of allStationsFromApi) {
      if (
        !eppStationsInCustomerDocument.some(
          singleEppStationInCustomerDocument =>
            singleEppStationInCustomerDocument === singleEppStationFromApi
        )
      ) {
        delete eppDataGroupedByLocationId[singleEppStationFromApi];
      }
    }
  }

  getProductTypeKey(productGroupToFilterOut: string): string {
    // every product holds the productType under a different key,
    // this function takes in the name under which the products are safed in customerDocument (e.g. egpProducts or eopProducts)
    // and returns the right key for productType
    // Example: egpProducts --> have egpType; eopProducts --> have eopTypes;
    let productTypeKey: string;

    switch (productGroupToFilterOut) {
      case 'egpProducts':
        productTypeKey = 'egpType';
        break;
      case 'egpSummerProducts':
        productTypeKey = 'egpType';
        break;
      case 'eopProducts':
        productTypeKey = 'eopType';
        break;
      case 'egwProducts':
        productTypeKey = 'ProductWithNoProductType';
        break;
      case 'egwSummerProducts':
        productTypeKey = 'ProductWithNoProductType';
        break;
      default:
        console.log('Error in getProductTypeNames(), Product not defined');
    }
    return productTypeKey;
  }

  checkForUnwantedProductTypes(
    product: object,
    keyForProductType: string
  ): boolean {
    // returns true when unwanted productTypes are found in the given product
    switch (keyForProductType) {
      // unwanted productTypes in EOP
      case 'eopType':
        if (
          product[keyForProductType] === 'FIFTEEN_DAY' ||
          product[keyForProductType] === 'FIFTEEN_DAY_ALL' ||
          product[keyForProductType] === 'FIFTEEN_DAY_SUMMER'
        ) {
          return true;
        }
        break;
      default:
        return false;
    }
  }

  addEopStationsToProductId(
    customerDocument: object,
    filteredEopProducts: object
  ): object {
    // EXPLANATION: returns eopsWithStations; takes the filteredEopProducts (contains all eopProducts in customerDocument) and adds
    //               the stations that belong to each productId & productType and stores them in eopsWithStations;
    //  ---------------------------------------------------------------------------------------------------------------------------
    // filteredEopProducts --> object which contains all the eopProducts in customerDocument,
    //                         should looks like this e.g. --> { "1756": "EIGHT_DAY", "2144": "TWO_DAY", ...}
    // eopsWithStations    --> contains all productsId's with their eopType and all the stations
    //                         should look like this e.g. --> { "1756": "EIGHT_DAY": ["stationName": "Boryspil", "stationId": "33347"],
    //                                                        "2144": "TWO_DAY": ["stationName": "Amsterdam", "stationId": "100021"], ...}

    const eopsWithStations = {};
    const allEopProducts = customerDocument['eopProducts'];

    for (const singleProduct of allEopProducts) {
      if (filteredEopProducts.hasOwnProperty(singleProduct['id'])) {
        const productId = singleProduct['id'];
        const productType = singleProduct['eopType'];
        eopsWithStations[productId] = {};
        eopsWithStations[productId][productType] = [];
        for (const singleStation of singleProduct.stations) {
          eopsWithStations[productId][productType].push({
            stationName: singleStation['overwriteStationName'],
            stationId: singleStation['stationId'],
          });
        }
      }
    }

    return eopsWithStations;
  }

  transformProductTypes(
    productGroupName: string,
    productToTranslate: object
  ): object {
    // EXPLANATION: returns the given productToTranslate with replaces productTypes, replaces e.g. 'EIGHT_DAY' with 'eop8'
    //              needed because API needs the predefined productTypes for request
    //              'EIGHT_DAY' --> how productTypes are in customerDocument
    //              'eop8'      --> predefined productTypes in (egpTypes, egpSummerTypes, eopTypes)
    // --------------------------------------------------------------------------------------------------------------------------------

    const allProductIds = Object.keys(productToTranslate);

    // EOP TRANSLATION
    if (productGroupName === 'eopProducts') {
      for (const productId of allProductIds) {
        const objectStoredInId = productToTranslate[productId]; // get object that is stored inside this productId
        // key of objectStoredInId is the eopType e.g. EIGHT_DAY, [0] because every productId should only have one productType
        const eopTypeInProductToTransform = Object.keys(objectStoredInId)[0];
        const preDefinedEopTypes = Object.keys(this.eopTypes); // keys of this.eopTypes --> [ TWO_DAY, FOUR_DAY, ...]

        // in case eopType is not pre-defined in this.eopTypes
        if (preDefinedEopTypes.indexOf(eopTypeInProductToTransform) < 0) {
          console.log(
            'Error: eopType',
            eopTypeInProductToTransform,
            'is not predefined in this.eopTypes Function --> transformProductTypes()'
          );
          continue;
        }

        // check for match in pre-defined eopTypes with the one that should be transformed (eopTypeInProductToTransform)
        for (const [key, value] of Object.entries(this.eopTypes)) {
          if (key === eopTypeInProductToTransform) {
            // rename eopType; assign old values to new eopType (e.g. eop8) and delete old one (e.g. EIGHT_DAY)
            // e.g. (new eopType) --> {productId: {eop8: [stations...] }} ; (old eopType) --> {productId: {EIGHT_DAY: [stations...] }}
            productToTranslate[productId][value] =
              productToTranslate[productId][eopTypeInProductToTransform];
            delete productToTranslate[productId][eopTypeInProductToTransform];
            break;
          }
        }
      }
    }
    // EGP & EGP-SUMMER TRANSLATION
    else if (
      productGroupName === 'egpProducts' ||
      productGroupName === 'egpSummerProducts'
    ) {
      for (const productId of allProductIds) {
        const objectStoredInId = productToTranslate[productId];

        const preDefinedSet =
          productGroupName === 'egpProducts'
            ? this.egpTypes
            : this.egpTypesSummer; // either egpTypes or egpTypesSummer

        const preDefinedTypesKeys = Object.keys(preDefinedSet);

        // in case egpType is not pre-defined in this.egpTypes
        if (preDefinedTypesKeys.indexOf(objectStoredInId) < 0) {
          console.log(
            'Error: egpType/egpSummerType',
            objectStoredInId,
            'is not predefined in egpTypes/egpSummerType; ' +
              'Function --> transformProductTypes()'
          );
          continue;
        }

        // check for match in pre-defined egpTypes with the one that should be transformed (eopTypeInProductToTransform)
        for (const [key, value] of Object.entries(preDefinedSet)) {
          if (key === objectStoredInId) {
            const productType = value.toString();
            productToTranslate[productId] = productType;
            break;
          }
        }
      }
    }
    return productToTranslate;
  }

  sortApiDataByLocation(apiData): object {
    // EXPLANATION: sorts the data coming from API by LocationId, under the locationId the data gets further grouped by productType
    // apiDataSortedByLocation could look like this -->
    // {locationId:{eops8: [all pdf data for locationId & eops8]}, eops2: [all pdf data for locationId & eops2]}, locationId2:{eops8:[...]}}
    // apiData is an Array that itself stores an Array for every Product that got requested from API
    // inside the productArrays are the PDF Objects stored that the API delivered
    // apiData should look like this --> apiData = apiArray-->[productArray-->[{singlePDFsOfProduct},{},{}],productArray-->[{},{},...],...]
    const apiDataSortedByLocation = {};
    /*TODO: dataForProductId genau erklären */
    for (const dataForProductId of apiData) {
      if (!Array.isArray(dataForProductId) || !dataForProductId.length) {
        continue;
      } // skip loop when array is empty
      for (const singleDataSet of dataForProductId) {
        const locationId = singleDataSet.location;
        const productType = this.addSeparatorBetweenProductTypeAndResolution(
          singleDataSet.type
        );
        // locationId already exists in apiDataSortedByLocation?
        if (!apiDataSortedByLocation.hasOwnProperty(locationId)) {
          apiDataSortedByLocation[locationId] = {};
        }
        // productType already exists under productId?
        if (!apiDataSortedByLocation[locationId].hasOwnProperty(productType)) {
          apiDataSortedByLocation[locationId][productType] = [];
        }
        apiDataSortedByLocation[locationId][productType].push(singleDataSet);
      }
    }
    return apiDataSortedByLocation;
  }

  onStationSelect(event): void {
    // takes in the event and depending on which element it got triggered it sets the stationsId's
    const idOfElement = event.source.id; // id of element where event got triggered

    if (event.isUserInput) {
      switch (idOfElement) {
        case 'eopStationSelect':
          this.selectedEopStationId.set(event.source.value);
          // sets the eopType to first of the selected station
          this.selectedEopType.set(
            Object.keys(
              this.eopDataGroupedByLocationId()[this.selectedEopStationId()]
            )[0]
          );
          break;
        case 'egpStationSelect':
          this.selectedEgpStationId.set(event.source.value);
          this.selectedEgpType.set(
            Object.keys(
              this.egpDataGroupedByLocationId()[this.selectedEgpStationId()]
            )[0]
          );
          break;
        case 'egpSummerStationSelect':
          this.selectedEgpSummerStationId.set(event.source.value);
          this.selectedEgpSummerType.set(
            Object.keys(
              this.egpSummerDataGroupedByLocationId()[
                this.selectedEgpSummerStationId()
              ]
            )[0]
          );
          break;
        default:
          console.log('Error in onStationSelect()');
      }
    }
  }

  checkForContent(dataGroupedByLocationId: object): boolean {
    // simply checks if object holds any data; needed to decide if a card should be shown or not
    if (Object.keys(dataGroupedByLocationId).length === 0) {
      return;
    } // dataGroupedByLocationId holds no data
    else if (Object.keys(dataGroupedByLocationId).length >= 1) {
      return true;
    } // dataGroupedByLocationId holds data
  }

  downloadPdf(pathOfPdf: string): void {
    const url = this.archiveService.buildPdfUrl(pathOfPdf);
    window.open(url);
  }
  downloadYearlyReport(): void {
    const url =
      'https://s3.eu-west-1.amazonaws.com/app.einsatzwetter.de/pdf/Gladheid+Winteroverzicht+2023_2024+Zld+(einsatzwetter).pdf';
    window.open(url);
  }
  downloadDummyReport(reportType: string): void {
    const reports = {
      yearly: 'Gladheid+Winteroverzicht+2023_2024+Zld+(einsatzwetter).pdf',
      weekly: 'Weekoverzicht+Gelderland.pdf',
    };
    const url = 'https://s3.eu-west-1.amazonaws.com/app.einsatzwetter.de/pdf/';
    const fullUrl = url + reports[reportType];
    window.open(fullUrl);
  }

  setInitialSelectedStationAndType(
    productToInitialize: string,
    dataGroupedByLocationId: object
  ): void {
    if (Object.keys(dataGroupedByLocationId).length === 0) {
      return;
    }
    switch (productToInitialize) {
      case 'eop':
        const eopDataGroupedByLocationId = this.eopDataGroupedByLocationId();
        this.selectedEopStationId.set(
          Object.keys(eopDataGroupedByLocationId)[0]
        );
        this.selectedEopType.set(
          Object.keys(
            eopDataGroupedByLocationId[this.selectedEopStationId()]
          )[0]
        );
        break;
      case 'epp':
        const eppDataGroupedByLocationId = this.eppDataGroupedByLocationId();
        this.selectedEppStationId.set(
          Object.keys(eppDataGroupedByLocationId)[0]
        );
        this.selectedEppType.set(
          Object.keys(
            eppDataGroupedByLocationId[this.selectedEppStationId()]
          )[0]
        );
        break;
      case 'egp':
        this.selectedEgpStationId.set(
          Object.keys(this.egpDataGroupedByLocationId())[0]
        );
        this.selectedEgpType.set(
          Object.keys(
            this.egpDataGroupedByLocationId()[this.selectedEgpStationId()]
          )[0]
        );
        break;
      case 'egpSummer':
        const egpSummerDataGroupedByLocationId =
          this.egpSummerDataGroupedByLocationId();
        this.selectedEgpSummerStationId.set(
          Object.keys(egpSummerDataGroupedByLocationId)[0]
        );
        this.selectedEgpSummerType.set(
          Object.keys(
            egpSummerDataGroupedByLocationId[this.selectedEgpSummerStationId()]
          )[0]
        );
        break;
      case 'egw':
        const filteredWarnings = this.filteredWarnings();
        const allAreaIds = Object.keys(filteredWarnings);
        for (const singleAreaId of allAreaIds) {
          if (filteredWarnings[singleAreaId].length > 0) {
            this.selectedWarningAreaId.set(singleAreaId);
          }
        }
        break;
      default:
        console.log(
          'Error: could not set initial stationId + productType in setInitialSelectedStationAndType()'
        );
    }
  }

  onZipDownload(productGroupName: string): void {
    /*TODO: eigene funktion für dateinamenerstellung*/
    let allNeededStations = [];
    let zipFolderName = '';
    let basicFilename = '';
    switch (productGroupName) {
      case 'eopProducts':
        const selectedEopStationId = this.selectedEopStationId();
        allNeededStations =
          this.eopDataGroupedByLocationId()[selectedEopStationId][
            this.selectedEopType()
          ];
        zipFolderName =
          this.translate.instant('PRODUCT.EOP.TITLE') +
          ' ' +
          this.eopStationNames()[selectedEopStationId] +
          '(' +
          this.dateRange.value.start.format('DD/MM/YYYY') +
          ' – ' +
          this.dateRange.value.end.format('DD/MM/YYYY') +
          ')';
        basicFilename = this.eopStationNames()[selectedEopStationId];
        break;
      case 'egpProducts':
        const selectedEgpStationId = this.selectedEgpStationId();
        allNeededStations =
          this.egpDataGroupedByLocationId()[selectedEgpStationId][
            this.selectedEgpType()
          ];
        zipFolderName =
          this.translate.instant('PRODUCT.EGP.TITLE') +
          ' ' +
          this.egpAreaNames()[selectedEgpStationId] +
          '(' +
          this.dateRange.value.start.format('DD/MM/YYYY') +
          ' – ' +
          this.dateRange.value.end.format('DD/MM/YYYY') +
          ')';
        basicFilename = this.egpAreaNames()[selectedEgpStationId];
        break;

      case 'egpSummerProducts':
        const selectedEgpSummerStationId = this.selectedEgpSummerStationId();
        allNeededStations =
          this.egpSummerDataGroupedByLocationId()[selectedEgpSummerStationId][
            this.selectedEgpSummerType()
          ];
        zipFolderName =
          this.translate.instant('PRODUCT.EGP-SUMMER.TITLE') +
          ' ' +
          this.egpSummerAreaNames()[selectedEgpSummerStationId] +
          '(' +
          this.dateRange.value.start.format('DD/MM/YYYY') +
          ' – ' +
          this.dateRange.value.end.format('DD/MM/YYYY') +
          ')';
        basicFilename = this.egpSummerAreaNames()[selectedEgpSummerStationId];
        break;

      case 'eppProducts':
        const selectedEppStationId = this.selectedEppStationId();
        allNeededStations =
          this.eppDataGroupedByLocationId()[selectedEppStationId][
            this.selectedEppType()
          ];
        zipFolderName =
          this.translate.instant('PRODUCT.EPP.TITLE') +
          ' ' +
          this.eppStationNames()[selectedEppStationId] +
          '(' +
          this.dateRange.value.start.format('DD/MM/YYYY') +
          ' – ' +
          this.dateRange.value.end.format('DD/MM/YYYY') +
          ')';
        basicFilename = this.eppStationNames()[selectedEppStationId];
        break;

      default:
        console.log('Error in onZipDownload(), productGroupName not defined');
    }

    // create zip file
    const jszip = new JSZip();
    let count = 0;

    for (const singleStation of allNeededStations) {
      const url = this.archiveService.buildPdfUrl(singleStation['path']);
      const dateOfPdf = moment(singleStation['dtg']).format('DD.MM.YY--HH-mm');

      JSZipUtils.getBinaryContent(url, (err, data) => {
        if (data) {
          count++;
          const filename = dateOfPdf + '--' + basicFilename + '.pdf';
          if (!filename) {
            console.log('Error: No Filename for ' + url);
          } else {
            jszip.file(filename, data, { binary: true });
            if (count === allNeededStations.length) {
              jszip.generateAsync({ type: 'blob' }).then(content => {
                FileSaver.saveAs(content, zipFolderName);
              });
            }
          }
        }
      });
    }
  }

  createCsvDataFromFilteredWarnings(areaIdToCreateCsvDataOf): Array<object> {
    const filteredWarnings = this.filteredWarnings();
    // brings the data from filteredWarnings for selected areaId in the right form for generating the csv
    // which only needs an object in the form of csvWarning
    const dataOfSelectedAreaId = filteredWarnings?.[areaIdToCreateCsvDataOf];
    if (!dataOfSelectedAreaId) {
      return [];
    }
    const dataForCsv = [];

    for (const singleWarning of dataOfSelectedAreaId) {
      const translatedWarningType = this.translate.instant(
        'PRODUCT.WARNING.TYPE.' + singleWarning.type
      );

      const csvWarning = {
        dtg: singleWarning.dtg.format('YYYY-MM-DD HH:mm'),
        type: translatedWarningType,
        message: singleWarning.message,
      };
      dataForCsv.push(csvWarning);
    }
    return dataForCsv;
  }

  onEopStationChange(selectedEopStationId: string): void {
    this.selectedEopType.set(
      Object.keys(this.eopDataGroupedByLocationId()[selectedEopStationId])[0]
    );
  }

  onEppStationChange(selectedEppStationId: string): void {
    this.selectedEppType.set(
      Object.keys(this.eppDataGroupedByLocationId()[selectedEppStationId])[0]
    );
  }

  /*====================================== TRANSLATION METHODS ==========================================================*/
  getEgpAreasNames(): Promise<Array<object>> {
    // EXPLANATION: requests the egpAreaData for winter(egpAreaData) & summer(egpSummerAreaData), once request is completed the data
    // gets handed over to this.sortOutAllIdsAndNames

    /*TODO: caching wieder reinnehmen
    // try to get area names first from cache
    this.egpAreaNames = this.cacheService.getCachedData('egp_area_names');
    this.egpSummerAreas = this.cacheService.getCachedData('egp_summer_area_names');*/

    if (this.egpAreaNames() && this.egpSummerAreaNames()) {
      return;
    }

    const promises = [];
    /*const apiAreaNameData = {};*/

    // WINTER
    if (!this.egpAreaNames()) {
      const apiAreaNameData = {};
      promises.push(
        new Promise((resolve, reject) => {
          this.egpService.getAllEgpAreas(EgpSeason.WINTER).then(data => {
            /*TODO: check how data looks like and then assign it to egpWinter correctly*/
            apiAreaNameData['egpAreaData'] = data;
            /*this.egpAreaNames = {...data};*/
            resolve(apiAreaNameData);
            /*this.sortOutEgpAreaNames(apiAreaNameData['egpAreaData']);*/
          });
        })
      );
    }

    // SUMMER
    if (!this.egpSummerAreaNames()) {
      const apiSummerAreaNameData = {};

      promises.push(
        new Promise((resolve, reject) => {
          this.egpService.getAllEgpAreas(EgpSeason.SUMMER).then(dataSummer => {
            // check if dataSummer has key 'egpareas' under which the data is stored
            // if (Object.keys(dataSummer).indexOf('egpareas') < 0) {
            apiSummerAreaNameData['egpSummerAreaData'] = dataSummer;
            // } else {
            //  apiSummerAreaNameData['egpSummerAreaData'] = dataSummer['egpareas'];
            // }
            resolve(apiSummerAreaNameData);
            /*this.sortOutEgpSummerAreaNames(apiAreaNameData['egpSummerAreaData']);*/
          });
        })
      );
    }

    return Promise.all(promises);
  }

  sortOutEgpAreaNames(allEgpAreaData: Array<object>): void {
    // sorts data from API request in getEgpAreaNames(), depending on if they are the area names for egp winter or egp summer
    // egpAreaNames & egpSummerAreaNames are needed to later translate id's to real names
    // egp area names for winter --> get sorted to this.egpAreaNames
    // egp area names for summer --> get sorted to this.egpSummerAreaNames
    // allEgpAreaData should look like --> allEgpAreaData = [{egpAreaData: [allEgpAreaData]}, {egpSummerAreaData: [allEgpSummerAreaData]}]

    for (const singleDataSet of allEgpAreaData) {
      // singleDataSet --> egpAreaData (winter) or egpSummerAreaData (summer)
      // for egpArea data (winter)
      if (singleDataSet.hasOwnProperty('egpAreaData')) {
        // check under which key
        this.egpAreaNames.set({});
        const egpAreaData = singleDataSet['egpAreaData'];
        for (const singleArea of egpAreaData) {
          const areaId = singleArea.id;
          const name = singleArea.name;
          if (!this.egpAreaNames().hasOwnProperty(areaId)) {
            this.egpAreaNames.update(egpAreaNames => {
              return { ...egpAreaNames, [areaId]: name };
            });
          }
        }
      }
      // for egpSummerAreaData
      else if (singleDataSet.hasOwnProperty('egpSummerAreaData')) {
        this.egpSummerAreaNames.set({});
        const egpSummerAreaData = singleDataSet['egpSummerAreaData'];
        for (const singleArea of egpSummerAreaData) {
          const areaId = singleArea.id;
          const name = singleArea.name;
          if (!this.egpSummerAreaNames().hasOwnProperty(areaId)) {
            this.egpSummerAreaNames.update(egpSummerAreaNames => {
              return { ...egpSummerAreaNames, [areaId]: name };
            });
          }
        }
      }
    }
  }

  getEopStationsNames(customerDocument: object): object {
    // loops through the eopData and puts stationId & name into
    const eopStationNames = {};

    if (!customerDocument.hasOwnProperty('eopProducts')) {
      return;
    }

    const eopProducts = customerDocument['eopProducts'];

    for (const singleProduct of eopProducts) {
      for (const singleStation of singleProduct.stations) {
        if (!eopStationNames.hasOwnProperty(singleStation.stationId)) {
          eopStationNames[singleStation.stationId] =
            singleStation.overwriteStationName;
        }
      }
    }
    return eopStationNames;
  }

  getEppStationsNames(customerDocument: object): object {
    // loops through the eopData and puts stationId & name into
    const eppStationNames = {};

    if (!customerDocument.hasOwnProperty('eppProducts')) {
      return;
    }

    const eppProducts = customerDocument['eppProducts'];

    for (const singleProduct of eppProducts) {
      for (const singleStation of singleProduct.stations) {
        if (!eppStationNames.hasOwnProperty(singleStation.stationId)) {
          eppStationNames[singleStation.stationId] =
            singleStation.overwriteStationName;
        }
      }
    }
    return eppStationNames;
  }

  /*====================================== PAGINATOR METHODS ==========================================================*/

  initTableData(): void {
    const filteredWarnings = this.filteredWarnings();
    if (filteredWarnings[this.selectedWarningAreaId()].length > 5) {
      this.paginatorDataWarnings.set([
        ...filteredWarnings[this.selectedWarningAreaId()].slice(0, 5),
      ]);
    } else if (filteredWarnings[this.selectedWarningAreaId()].length <= 5) {
      this.paginatorDataWarnings.set([
        ...filteredWarnings[this.selectedWarningAreaId()],
      ]);
    }
  }

  onChangeWarningAreaId(selectedAreaId): void {
    this.initTableData();
  }

  onChangePage(pageEvent: PageEvent): void {
    this.paginatorDataWarnings.set([
      ...this.filteredWarnings()[this.selectedWarningAreaId()].slice(
        pageEvent.pageIndex * pageEvent.pageSize,
        pageEvent.pageSize + pageEvent.pageIndex * pageEvent.pageSize
      ),
    ]);
  }

  /*====================================== WARNING ==========================================================*/

  getWarningsFromApi(limitOfRequestedWarnings: number = 2000): void {
    // EXPLANATION:
    if (this.egwProducts === null) {
      return;
    } // leave function when egwProducts is empty

    const allEgwProducts = this.customerDocument.egwProducts;

    for (const singleEgwProduct of allEgwProducts) {
      this.warnSeason = true;
      const areaId = singleEgwProduct.areaId;

      const areaName =
        this.egpAreaNames()[areaId] !== undefined
          ? this.egpAreaNames()[areaId] + ' (Winter)'
          : this.egpAreaNames()[areaId];

      // safe stationId and corresponing name, so that it later can be used to translate id to real name
      this.addToWarningStationNames(areaId, areaName);

      const timeRangesForProduct =
        this.archiveService.getArchiveTimeRangesForProduct(singleEgwProduct);
      for (const timeRange of timeRangesForProduct) {
        this.warningAreasAll.push({
          name: areaName,
          id: singleEgwProduct.areaId,
          start: timeRange.start,
          end: timeRange.end,
        });
      }

      // warningRegion with Interface WarnRegion required to request warningsForAllEgwAndEgwSummerProducts
      // for areaId via warningService.getWarning()
      const warningRegion: WarnRegion = {
        id: areaId.toString(),
        name: areaName,
        season: WarnSeason.WINTER,
      };

      this.warningsForAllEgwAndEgwSummerProducts[areaId] = [];

      this.warningService
        .getWarning(warningRegion, limitOfRequestedWarnings)
        .subscribe(
          data => {
            this.warningsForAllEgwAndEgwSummerProducts[areaId] = data;
          },
          error =>
            console.log('Error Archive Component getWarningsFromApi', error)
        );
    }
  }

  getWarningsSummerFromApi(limitOfRequestedWarnings: number = 2000): void {
    if (this.egwSummerProducts === null) {
      return;
    }

    const allEgwSummerProducts = this.customerDocument.egwSummerProducts;

    for (const singleEgwSummerProduct of allEgwSummerProducts) {
      // needed properties
      const productId = singleEgwSummerProduct.id;
      const areaName = singleEgwSummerProduct.points[0].searchName;
      this.warnSeason = false;

      // safe stationId and corresponing name, so that it later can be used to translate id to real name
      this.addToWarningStationNames(productId, areaName);

      const timeRanges = this.archiveService.getArchiveTimeRangesForProduct(
        singleEgwSummerProduct
      );
      for (const timeRange of timeRanges) {
        this.warningAreasAll.push({
          name: singleEgwSummerProduct.points[0].searchName.concat(' (Sommer)'),
          id: productId,
          start: timeRange.start,
          end: timeRange.end,
        });
      }

      const warningRegion: WarnRegion = {
        id: productId,
        name: areaName,
        season: WarnSeason.SUMMER,
      };

      this.warningsForAllEgwAndEgwSummerProducts[productId] = [];

      this.warningService
        .getWarning(warningRegion, limitOfRequestedWarnings)
        .subscribe(
          data => {
            this.warningsForAllEgwAndEgwSummerProducts[productId] = data;
          },
          error =>
            console.log(
              'Error Archive Component getWarningsSummerFromApi',
              error
            )
        );
    }
  }

  addToWarningStationNames(stationId, stationName): void {
    if (!this.warningStationNames().hasOwnProperty(stationId)) {
      this.warningStationNames.update(warningStationNames => {
        return { ...warningStationNames, [stationId]: stationName };
      });
    }
  }

  filterWarnings(warningData: object, startDate, endDate, filter?): void {
    // filters warningData for warnings in timerange between startDate & endDate; warnings within the timerange are put into
    // filteredWarnings under their areaId
    // filteredWarnings should look like this --> { "areaId": [{warning1}, {warning2}, .. ], "areaId": [{warning1}, {warning2}, .. ] }
    // (warningData should be this.warningsForAllEgwAndEgwSummerProducts)

    const allAreaIds = Object.keys(warningData);
    const fromDate = startDate;
    const toDate = endDate;

    const filtered = [];
    const csvData = [];

    // go through every id under which the warnings for that area are stored
    for (const singleAreaId of allAreaIds) {
      this.filteredWarnings.update(filteredWarnings => {
        filteredWarnings[singleAreaId] = [];
        return { ...filteredWarnings };
      });

      const allWarningsForThatArea = warningData[singleAreaId];

      for (const singleWarning of allWarningsForThatArea) {
        // go through all warnings stored under that id
        const dtgOfSingleWarning = moment(singleWarning.dtg);

        if (dtgOfSingleWarning.isBetween(fromDate, toDate)) {
          this.filteredWarnings.update(filteredWarnings => {
            filteredWarnings[singleAreaId] = [
              ...filteredWarnings[singleAreaId],
              singleWarning,
            ];
            return { ...filteredWarnings };
          });

          const csvLine = {
            dtg: singleWarning.dtg.format('YYYY-MM-DD HH:mm'),
            type: null,
            message: singleWarning.message,
          };

          /* TODO: instant */
          this.translate
            .get('PRODUCT.WARNING.TYPE.' + singleWarning.type)
            .subscribe(res => {
              csvLine.type = res;
              csvData.push(csvLine);
            });
        }
        this.csvWarnings[singleAreaId] = csvData;
      }
    }
  }

  addSeparatorBetweenProductTypeAndResolution(productType: string): string {
    const seperatedProduct = productType.split(/(\d+)/);
    if (Array.isArray(seperatedProduct) && seperatedProduct.length >= 2) {
      return `${seperatedProduct[0]}${seperatedProduct[1]},${seperatedProduct[2]}`;
    }
    return productType;
  }
}
